import React, { type PropsWithChildren, useContext, useState } from 'react';
import { type BackendExperiment } from '@atlassian/search-experiment';
import { SearchDialogAnalyticsContext } from '../../common/analytics';
import { type ABTest } from '../../common/clients';

import { useExperiments } from '../../common/experiment-provider';

export const DEFAULT_AB_TEST: ABTest = {
	abTestId: 'default',
	controlId: 'default',
	experimentId: 'default',
};

export interface ABTestContext {
	abTest: ABTest | null | BackendExperiment;
}

const ABTestContext = React.createContext<ABTestContext>({
	abTest: null,
});

const ABTestContextProviderStateless = ({ children }: PropsWithChildren) => {
	const { backendExperiment, frontendExperiments = {} } = useExperiments();
	const [abTest] = useState<ABTest | null | BackendExperiment>(() => {
		return backendExperiment || DEFAULT_AB_TEST;
	});

	return (
		<ABTestContext.Provider value={{ abTest }}>
			<SearchDialogAnalyticsContext
				analyticContext={{
					abTest: abTest || undefined,
					frontendExperiments: {
						activeExperiments: Object.keys(frontendExperiments).filter(
							(experiment) => frontendExperiments[experiment]?.serveExperiment,
						),
						...frontendExperiments,
					},
				}}
				nonPrivacySafeAnalyticContext={{}}
			>
				{children}
			</SearchDialogAnalyticsContext>
		</ABTestContext.Provider>
	);
};

export const ABTestContextProvider = (props: PropsWithChildren) => (
	<ABTestContextProviderStateless {...props} />
);

export const useABTest = () => {
	const { abTest } = useContext(ABTestContext);
	return abTest;
};

export const withABTestContext = <Props,>(
	WrappedComponent: React.ComponentType<Props & ABTestContext>,
): React.ComponentType<Props> => {
	return (props: Props) => <WrappedComponent {...props} {...useContext(ABTestContext)} />;
};
